import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SelfCheckIn() {
  const { id } = useParams();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('email'); // email | loading | success | already | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [eventTitle, setEventTitle] = useState('');

  useEffect(() => {
    axios.get(`${API}/events/${id}`).then(r => setEventTitle(r.data.title)).catch(() => {});
  }, [id]);

  const handleCheckIn = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setStep('loading');
    try {
      const res = await axios.post(`${API}/events/${id}/self-checkin`, { email: trimmed });
      setResult(res.data);
      setStep(res.data.status === 'already_checked_in' ? 'already' : 'success');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong';
      setErrorMsg(detail);
      setStep('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="self-checkin-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-medium tracking-wider uppercase mb-4">
            Guest Check-In
          </div>
          {eventTitle && <h1 className="text-2xl font-bold text-[#EAE7E1] tracking-tight">{eventTitle}</h1>}
        </div>

        {/* Email Step */}
        {step === 'email' && (
          <form onSubmit={handleCheckIn} className="space-y-4" data-testid="checkin-email-form">
            <div>
              <label className="block text-sm text-[#AFAFAF] mb-2">Enter your email to check in</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
                className="bg-[#0A0A0A] border-[#1A1A1A] text-[#EAE7E1] placeholder:text-[#555] rounded-sm h-12 text-base"
                data-testid="checkin-email-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm font-bold text-base"
              data-testid="checkin-submit-button"
            >
              Check In
            </Button>
          </form>
        )}

        {/* Loading */}
        {step === 'loading' && (
          <div className="text-center py-12" data-testid="checkin-loading">
            <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto mb-4" />
            <p className="text-[#AFAFAF]">Verifying...</p>
          </div>
        )}

        {/* Success */}
        {step === 'success' && result && (
          <div className="text-center py-8 space-y-4" data-testid="checkin-success">
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-[#EAE7E1]">You're checked in!</h2>
            <p className="text-[#AFAFAF]">Welcome, {result.guest_name}</p>
            {result.event_title && <p className="text-sm text-[#777]">{result.event_title}</p>}
          </div>
        )}

        {/* Already checked in */}
        {step === 'already' && result && (
          <div className="text-center py-8 space-y-4" data-testid="checkin-already">
            <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-[#D4AF37]" />
            </div>
            <h2 className="text-2xl font-bold text-[#EAE7E1]">Already checked in</h2>
            <p className="text-[#AFAFAF]">Welcome back, {result.guest_name}</p>
          </div>
        )}

        {/* Error / Not on list */}
        {step === 'error' && (
          <div className="text-center py-8 space-y-4" data-testid="checkin-error">
            <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
              {errorMsg.includes('not started') || errorMsg.includes('closed') ? (
                <Clock className="w-12 h-12 text-amber-400" />
              ) : (
                <XCircle className="w-12 h-12 text-red-400" />
              )}
            </div>
            <h2 className="text-xl font-bold text-[#EAE7E1]">
              {errorMsg.includes('not on the guest list') ? 'Not on the guest list' :
               errorMsg.includes('not started') ? 'Check-in not started' :
               errorMsg.includes('closed') ? 'Check-in closed' : 'Unable to check in'}
            </h2>
            <p className="text-[#AFAFAF] text-sm">{errorMsg}</p>
            <Button
              onClick={() => { setStep('email'); setEmail(''); setErrorMsg(''); }}
              variant="outline"
              className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm mt-4"
              data-testid="checkin-try-again"
            >
              Try again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
